/* eslint-disable react/prop-types */
import React, { memo, useCallback, useEffect, useId, useRef, useState } from "react";

const WIDTH = 220;
const HEIGHT = 150;
const PADDING = 12;

function WorkflowOverview({ engine, onInteract }) {
  const maskId = useId().replace(/:/g, "");
  const svgRef = useRef(null);
  const isPanning = useRef(false);
  const [overview, setOverview] = useState(null);

  const updateOverview = useCallback(() => {
    const model = engine?.getModel?.() || engine?.model;
    const canvas = engine?.getCanvas?.();

    if (!model || !canvas?.clientWidth || !canvas?.clientHeight) {
      return;
    }

    const zoom = model.getZoomLevel() / 100;
    if (!zoom) {
      return;
    }

    const viewport = {
      x: -model.getOffsetX() / zoom,
      y: -model.getOffsetY() / zoom,
      width: canvas.clientWidth / zoom,
      height: canvas.clientHeight / zoom,
    };
    const nodes = model.getNodes().map((node) => {
      const dimensions = engine.getNodeDimensions?.(node) || {};
      return {
        x: node.getX(),
        y: node.getY(),
        width: dimensions.width || node.width || 1,
        height: dimensions.height || node.height || 1,
      };
    });
    const bounds = [...nodes, viewport].reduce(
      (currentBounds, item) => ({
        minX: Math.min(currentBounds.minX, item.x),
        minY: Math.min(currentBounds.minY, item.y),
        maxX: Math.max(currentBounds.maxX, item.x + item.width),
        maxY: Math.max(currentBounds.maxY, item.y + item.height),
      }),
      {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
      }
    );
    const diagramWidth = Math.max(bounds.maxX - bounds.minX, 1);
    const diagramHeight = Math.max(bounds.maxY - bounds.minY, 1);
    const scale = Math.min(
      (WIDTH - PADDING * 2) / diagramWidth,
      (HEIGHT - PADDING * 2) / diagramHeight
    );
    const offsetX = (WIDTH - diagramWidth * scale) / 2 - bounds.minX * scale;
    const offsetY = (HEIGHT - diagramHeight * scale) / 2 - bounds.minY * scale;

    setOverview({
      nodes: nodes.map((node) => ({
        x: node.x * scale + offsetX,
        y: node.y * scale + offsetY,
        width: node.width * scale,
        height: node.height * scale,
      })),
      viewport: {
        x: viewport.x * scale + offsetX,
        y: viewport.y * scale + offsetY,
        width: viewport.width * scale,
        height: viewport.height * scale,
      },
      scale,
      offsetX,
      offsetY,
    });
  }, [engine]);

  const panToOverviewPoint = useCallback(
    (event) => {
      if (!overview || !svgRef.current) {
        return;
      }

      onInteract?.();
      const svgBounds = svgRef.current.getBoundingClientRect();
      const x = ((event.clientX - svgBounds.left) / svgBounds.width) * WIDTH;
      const y = ((event.clientY - svgBounds.top) / svgBounds.height) * HEIGHT;
      const model = engine.getModel?.() || engine.model;
      const canvas = engine.getCanvas?.();
      const zoom = model.getZoomLevel() / 100;
      const diagramX = (x - overview.offsetX) / overview.scale;
      const diagramY = (y - overview.offsetY) / overview.scale;

      model.setOffset(
        canvas.clientWidth / 2 - diagramX * zoom,
        canvas.clientHeight / 2 - diagramY * zoom
      );
      engine.repaintCanvas();
    },
    [engine, onInteract, overview]
  );

  const handlePointerDown = useCallback(
    (event) => {
      isPanning.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      panToOverviewPoint(event);
    },
    [panToOverviewPoint]
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (isPanning.current) {
        panToOverviewPoint(event);
      }
    },
    [panToOverviewPoint]
  );

  const handlePointerUp = useCallback((event) => {
    isPanning.current = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  useEffect(() => {
    updateOverview();
    const listener = engine.model.registerListener({
      offsetUpdated: updateOverview,
      zoomUpdated: updateOverview,
    });
    const resizeObserver = new ResizeObserver(updateOverview);
    const canvas = engine.getCanvas?.();
    if (canvas) {
      resizeObserver.observe(canvas);
    }
    const frame = requestAnimationFrame(updateOverview);

    return () => {
      listener?.deregister?.();
      resizeObserver.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [engine, updateOverview]);

  if (!overview) {
    return null;
  }

  return (
    <div className="workflow-overview" aria-label="Workflow diagram overview">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <title>Workflow diagram overview</title>
        <rect width={WIDTH} height={HEIGHT} className="workflow-overview__background" />
        {overview.nodes.map((node, index) => (
          <rect
            key={`${node.x}-${node.y}-${index}`}
            className="workflow-overview__node"
            {...node}
          />
        ))}
        <mask id={maskId}>
          <rect width={WIDTH} height={HEIGHT} fill="white" />
          <rect {...overview.viewport} fill="black" />
        </mask>
        <rect
          width={WIDTH}
          height={HEIGHT}
          className="workflow-overview__shade"
          mask={`url(#${maskId})`}
        />
        <rect className="workflow-overview__viewport" {...overview.viewport} />
      </svg>
    </div>
  );
}

export default memo(WorkflowOverview);
